import { Component, HostListener, OnInit, OnDestroy, AfterViewInit, ViewChild, ElementRef } from '@angular/core';

type Dir = 'up' | 'down' | 'left' | 'right' | 'none';

interface Cell {
	top: number;
	left: number;
}

interface Pellet {
	top: number;
	left: number;
	power: boolean;
}

interface Ghost {
	name: string;
	color: string;
	top: number;
	left: number;
	dir: Dir;
	spawnRow: number;
	spawnCol: number;
	cornerRow: number;
	cornerCol: number;
	frightened: boolean;
	eaten: boolean;
}

interface Popup {
	id: number;
	top: number;
	left: number;
	text: string;
}

/**
 * A fully-featured, arcade-style Pac-Man.
 *
 * Highlights over a naive implementation:
 *  - Continuous, cell-by-cell movement driven by a single game loop, with a
 *    queued next-direction so turns feel responsive (classic pre-turn feel).
 *  - Four ghosts with distinct personalities (Blinky/Pinky/Inky/Clyde),
 *    alternating global scatter/chase modes, plus a frightened mode.
 *  - Power pellets that make ghosts edible with an escalating 200-1600 combo.
 *  - Tunnel wrap-around on the middle row, lives, levels, high score
 *    (persisted to localStorage), a HUD, on-screen touch controls, overlays
 *    and optional WebAudio sound effects.
 *
 * Pellets and ghosts are only ever placed on cells reachable from Pac-Man's
 * start (BFS flood fill), so every level is guaranteed winnable even if the
 * maze contains isolated pockets.
 */
@Component({
	selector: 'app-pacman',
	templateUrl: './pacman.component.html',
	styleUrls: ['./pacman.component.scss'],
})
export class PacmanComponent implements OnInit, OnDestroy, AfterViewInit {
	readonly gridSize = 30; // px per cell — matches the scss layout

	@ViewChild('gameBoard') gameBoard!: ElementRef<HTMLDivElement>;

	// 1 = wall, 0 = path. 11 rows x 17 cols. The middle row (index 5) is open
	// at both horizontal ends to form the wrap-around tunnel.
	maze: number[][] = [
		[1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
		[1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 1],
		[1, 0, 1, 1, 1, 0, 1, 1, 1, 1, 1, 1, 0, 1, 1, 0, 1],
		[1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1],
		[1, 1, 1, 1, 1, 0, 1, 1, 1, 1, 1, 1, 0, 1, 1, 1, 1],
		[0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
		[1, 0, 1, 0, 1, 0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 1],
		[1, 0, 1, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0, 1],
		[1, 0, 1, 0, 1, 1, 1, 0, 1, 0, 1, 1, 1, 0, 1, 0, 1],
		[1, 0, 1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0, 1],
		[1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
	];

	rows = this.maze.length;
	cols = this.maze[0].length;

	// Positions are stored in pixels (always a multiple of gridSize) so exact
	// equality collision checks stay reliable and the template top.px/left.px
	// bindings keep working.
	pacmanStart: Cell = { top: this.gridSize, left: this.gridSize };
	pacman: Cell = { top: this.gridSize, left: this.gridSize };
	pacDir: Dir = 'none';
	pacNextDir: Dir = 'none';

	// Swipe/tap tracking for touch (and mouse) control on the board itself.
	private pointerStartX = 0;
	private pointerStartY = 0;
	private pointerActive = false;

	pellets: Pellet[] = [];
	ghosts: Ghost[] = [];
	popups: Popup[] = [];

	score = 0;
	highScore = 0;
	lives = 3;
	level = 1;
	timer = 0;

	// 'ready' waits for the first input; 'dying'/'levelclear' are brief
	// transition states; the loop only advances during 'playing'.
	gameState: 'ready' | 'playing' | 'paused' | 'dying' | 'levelclear' | 'gameover' = 'ready';
	soundOn = true;

	private mode: 'scatter' | 'chase' = 'scatter';
	private modeTicks = 0;
	private frightenedTicks = 0;
	private ghostCombo = 0;
	private tickCount = 0;
	private tickMs = 170;
	private popupId = 0;

	private loop: any = null;
	private timerInterval: any = null;
	private audioCtx: any = null;

	private readonly ghostSpecs = [
		{ name: 'blinky', color: '#ff3b30', corner: [0, this.cols - 1] },
		{ name: 'pinky', color: '#ff7ce5', corner: [0, 0] },
		{ name: 'inky', color: '#3be6ff', corner: [this.rows - 1, this.cols - 1] },
		{ name: 'clyde', color: '#ffb03b', corner: [this.rows - 1, 0] },
	];

	private readonly deltas: Record<Dir, [number, number]> = {
		up: [-1, 0],
		down: [1, 0],
		left: [0, -1],
		right: [0, 1],
		none: [0, 0],
	};

	// --- Lifecycle ----------------------------------------------------------

	ngOnInit(): void {
		this.highScore = this.loadHighScore();
		this.initLevel();
		this.loop = setInterval(() => this.tick(), this.tickMs);
		this.timerInterval = setInterval(() => {
			if (this.gameState === 'playing') {
				this.timer++;
			}
		}, 1000);
	}

	ngAfterViewInit(): void {
		setTimeout(() => this.focusGame(), 100);
	}

	focusGame(): void {
		this.gameBoard?.nativeElement?.focus();
	}

	ngOnDestroy(): void {
		clearInterval(this.loop);
		clearInterval(this.timerInterval);
		if (this.audioCtx && typeof this.audioCtx.close === 'function') {
			this.audioCtx.close();
		}
	}

	// --- Maze helpers -------------------------------------------------------

	private isWallCell(row: number, col: number): boolean {
		if (row < 0 || row >= this.rows) {
			return true;
		}
		const wrapped = this.wrapCol(col);
		return this.maze[row][wrapped] === 1;
	}

	// Horizontal wrap for the tunnel row; vertical never wraps.
	private wrapCol(col: number): number {
		if (col < 0) {
			return this.cols - 1;
		}
		if (col >= this.cols) {
			return 0;
		}
		return col;
	}

	// BFS from Pac-Man's start; only these cells may hold pellets/ghosts.
	private reachableCells(): Cell[] {
		const key = (top: number, left: number): string => `${top},${left}`;
		const startCell = { ...this.pacmanStart };
		const seen = new Set<string>([key(startCell.top, startCell.left)]);
		const queue: Cell[] = [startCell];
		const cells: Cell[] = [];
		while (queue.length) {
			const cur = queue.shift() as Cell;
			cells.push(cur);
			for (const dir of ['up', 'down', 'left', 'right'] as Dir[]) {
				const [dr, dc] = this.deltas[dir];
				const nextRow = cur.top / this.gridSize + dr;
				const nextCol = cur.left / this.gridSize + dc;
				if (this.isWallCell(nextRow, nextCol)) {
					continue;
				}
				const cell = { top: nextRow * this.gridSize, left: this.wrapCol(nextCol) * this.gridSize };
				if (!seen.has(key(cell.top, cell.left))) {
					seen.add(key(cell.top, cell.left));
					queue.push(cell);
				}
			}
		}
		return cells;
	}

	// --- Level setup --------------------------------------------------------

	private initLevel(): void {
		const reachable = this.reachableCells();
		this.buildPellets(reachable);
		this.spawnGhosts(reachable);
		this.resetActors();
		this.mode = 'scatter';
		this.modeTicks = 0;
		this.frightenedTicks = 0;
		this.ghostCombo = 0;
		this.tickCount = 0;
		this.popups = [];
		this.tickMs = Math.max(90, 170 - (this.level - 1) * 12);
	}

	private buildPellets(reachable: Cell[]): void {
		const start = this.pacmanStart;
		const open = reachable.filter((c) => !(c.top === start.top && c.left === start.left));
		// Power pellets go on the reachable cell nearest each maze corner.
		const corners: [number, number][] = [
			[0, 0],
			[0, this.cols - 1],
			[this.rows - 1, 0],
			[this.rows - 1, this.cols - 1],
		];
		const powerKeys = new Set<string>();
		for (const [cr, cc] of corners) {
			let best: Cell | null = null;
			let bestDist = Infinity;
			for (const cell of open) {
				const dist = Math.abs(cell.top / this.gridSize - cr) + Math.abs(cell.left / this.gridSize - cc);
				if (dist < bestDist) {
					bestDist = dist;
					best = cell;
				}
			}
			if (best) {
				powerKeys.add(`${best.top},${best.left}`);
			}
		}
		this.pellets = open.map((c) => ({
			top: c.top,
			left: c.left,
			power: powerKeys.has(`${c.top},${c.left}`),
		}));
	}

	private spawnGhosts(reachable: Cell[]): void {
		const start = this.pacmanStart;
		// Prefer cells that are a good distance from Pac-Man so ghosts do not
		// spawn on top of the player.
		const far = reachable
			.filter(
				(c) =>
					Math.abs(c.top - start.top) + Math.abs(c.left - start.left) > 5 * this.gridSize
			)
			.sort(
				(a, b) =>
					Math.abs(b.top - start.top) + Math.abs(b.left - start.left) -
					(Math.abs(a.top - start.top) + Math.abs(a.left - start.left))
			);
		const pool = far.length ? far : reachable;
		this.ghosts = this.ghostSpecs.map((spec, i) => {
			const cell = pool[Math.floor((pool.length / this.ghostSpecs.length) * i)] || pool[0];
			return {
				name: spec.name,
				color: spec.color,
				top: cell.top,
				left: cell.left,
				dir: 'up',
				spawnRow: cell.top / this.gridSize,
				spawnCol: cell.left / this.gridSize,
				cornerRow: spec.corner[0],
				cornerCol: spec.corner[1],
				frightened: false,
				eaten: false,
			};
		});
	}

	private resetActors(): void {
		this.pacman = { ...this.pacmanStart };
		this.pacDir = 'none';
		this.pacNextDir = 'none';
		for (const g of this.ghosts) {
			g.top = g.spawnRow * this.gridSize;
			g.left = g.spawnCol * this.gridSize;
			g.dir = 'up';
			g.frightened = false;
			g.eaten = false;
		}
	}

	// --- Input --------------------------------------------------------------

	@HostListener('document:keydown', ['$event'])
	handleKeyboardEvent(event: KeyboardEvent): void {
		if (document.activeElement !== this.gameBoard?.nativeElement) {
			return;
		}
		switch (event.key) {
		case 'ArrowUp':
			this.queueDir('up');
			event.preventDefault();
			break;
		case 'ArrowDown':
			this.queueDir('down');
			event.preventDefault();
			break;
		case 'ArrowLeft':
			this.queueDir('left');
			event.preventDefault();
			break;
		case 'ArrowRight':
			this.queueDir('right');
			event.preventDefault();
			break;
		case ' ':
		case 'p':
		case 'P':
			this.togglePause();
			event.preventDefault();
			break;
		case 'm':
		case 'M':
			this.toggleSound();
			break;
		case 'r':
		case 'R':
			this.restartGame();
			break;
		}
	}

	// Called by both keyboard and the on-screen touch d-pad.
	queueDir(dir: Dir): void {
		this.focusGame();
		if (this.gameState === 'gameover') {
			return;
		}
		if (this.gameState === 'ready') {
			this.gameState = 'playing';
		}
		if (this.gameState !== 'playing') {
			return;
		}
		this.pacNextDir = dir;
		// Turn immediately if the queued direction is already free — feels snappy.
		if (this.canMove(this.pacman, dir)) {
			this.pacDir = dir;
		}
	}

	// Swipe/tap on the board — pointer events unify touch, pen and mouse, so a
	// swipe steers Pac-Man on phones (with touch-action:none on the canvas) and
	// a tap starts the game when it is waiting.
	onBoardPointerDown(e: PointerEvent): void {
		this.focusGame();
		this.pointerActive = true;
		this.pointerStartX = e.clientX;
		this.pointerStartY = e.clientY;
	}

	onBoardPointerUp(e: PointerEvent): void {
		if (!this.pointerActive) {
			return;
		}
		this.pointerActive = false;
		const dx = e.clientX - this.pointerStartX;
		const dy = e.clientY - this.pointerStartY;
		const absX = Math.abs(dx);
		const absY = Math.abs(dy);
		// A short movement counts as a tap: use it to kick off a waiting game.
		if (absX < 16 && absY < 16) {
			if (this.gameState === 'ready') {
				this.gameState = 'playing';
			}
			return;
		}
		if (absX > absY) {
			this.queueDir(dx > 0 ? 'right' : 'left');
		} else {
			this.queueDir(dy > 0 ? 'down' : 'up');
		}
	}

	// --- Game loop ----------------------------------------------------------

	private tick(): void {
		if (this.gameState !== 'playing') {
			return;
		}
		this.tickCount++;

		// Pac-Man: honour the queued turn when it becomes possible.
		if (this.pacNextDir !== 'none' && this.canMove(this.pacman, this.pacNextDir)) {
			this.pacDir = this.pacNextDir;
		}
		const step = this.nextCell(this.pacman, this.pacDir);
		if (step) {
			this.pacman = step;
			this.eatPelletAt(this.pacman);
		}

		this.moveGhosts();
		if (this.checkGhostCollision()) {
			return; // life lost — state already changed
		}

		if (this.frightenedTicks > 0) {
			this.frightenedTicks--;
			if (this.frightenedTicks === 0) {
				this.ghosts.forEach((g) => (g.frightened = false));
			}
		}
		this.updateModeTimer();
		this.checkWin();
	}

	private canMove(from: Cell, dir: Dir): boolean {
		if (dir === 'none') {
			return false;
		}
		const [dr, dc] = this.deltas[dir];
		const row = from.top / this.gridSize + dr;
		const col = from.left / this.gridSize + dc;
		return !this.isWallCell(row, col);
	}

	private nextCell(from: Cell, dir: Dir): Cell | null {
		if (!this.canMove(from, dir)) {
			return null;
		}
		const [dr, dc] = this.deltas[dir];
		const row = from.top / this.gridSize + dr;
		const col = this.wrapCol(from.left / this.gridSize + dc);
		return { top: row * this.gridSize, left: col * this.gridSize };
	}

	// --- Pellets ------------------------------------------------------------

	private eatPelletAt(cell: Cell): void {
		const idx = this.pellets.findIndex((p) => p.top === cell.top && p.left === cell.left);
		if (idx === -1) {
			return;
		}
		const pellet = this.pellets[idx];
		this.pellets.splice(idx, 1);
		if (pellet.power) {
			this.score += 50;
			this.frightenGhosts();
			this.beep(180, 0.25, 'sawtooth', 0.06);
		} else {
			this.score += 10;
			this.beep(520 + (this.tickCount % 2) * 120, 0.05, 'square', 0.04);
		}
		this.commitHighScore();
	}

	private frightenGhosts(): void {
		this.frightenedTicks = this.ticksFor(6500);
		this.ghostCombo = 0;
		for (const g of this.ghosts) {
			if (!g.eaten) {
				g.frightened = true;
				g.dir = this.opposite(g.dir); // reverse, classic behaviour
			}
		}
	}

	// --- Ghosts -------------------------------------------------------------

	private moveGhosts(): void {
		const pacRow = this.pacman.top / this.gridSize;
		const pacCol = this.pacman.left / this.gridSize;
		for (const g of this.ghosts) {
			if (g.eaten) {
				// Race back to the spawn cell (eyes only), moving every tick.
				this.stepGhost(g, g.spawnRow, g.spawnCol, false);
				if (g.top === g.spawnRow * this.gridSize && g.left === g.spawnCol * this.gridSize) {
					g.eaten = false;
					g.frightened = false;
					g.dir = 'up';
				}
				continue;
			}
			if (g.frightened) {
				// Frightened ghosts flee and move at half speed.
				if (this.tickCount % 2 === 0) {
					this.stepGhost(g, pacRow, pacCol, true);
				}
				continue;
			}
			const [tr, tc] = this.mode === 'scatter'
				? [g.cornerRow, g.cornerCol]
				: this.chaseTarget(g, pacRow, pacCol);
			this.stepGhost(g, tr, tc, false);
		}
	}

	private chaseTarget(g: Ghost, pacRow: number, pacCol: number): [number, number] {
		const [dr, dc] = this.deltas[this.pacDir];
		switch (g.name) {
		case 'pinky':
			return [pacRow + dr * 4, pacCol + dc * 4]; // ambush ahead of Pac-Man
		case 'inky':
			return [pacRow + dr * 2, pacCol + dc * 2];
		case 'clyde': {
			const dist = Math.abs(g.top / this.gridSize - pacRow) + Math.abs(g.left / this.gridSize - pacCol);
			return dist > 8 ? [pacRow, pacCol] : [g.cornerRow, g.cornerCol]; // shy when close
		}
		default:
			return [pacRow, pacCol]; // blinky: straight for the player
		}
	}

	// One tile step toward (or, when fleeing, away from) a target, never into a
	// wall and never reversing unless it is the only option (dead end).
	private stepGhost(g: Ghost, targetRow: number, targetCol: number, flee: boolean): void {
		const row = g.top / this.gridSize;
		const col = g.left / this.gridSize;
		const reverse = this.opposite(g.dir);
		const order: Dir[] = ['up', 'left', 'down', 'right']; // classic tie-break
		let best: { dir: Dir; row: number; col: number } | null = null;
		let bestScore = flee ? -Infinity : Infinity;
		for (const dir of order) {
			if (dir === reverse) {
				continue;
			}
			const [dr, dc] = this.deltas[dir];
			const nextRow = row + dr;
			const nextCol = col + dc;
			if (this.isWallCell(nextRow, nextCol)) {
				continue;
			}
			const wrapped = this.wrapCol(nextCol);
			const dist = Math.abs(nextRow - targetRow) + Math.abs(wrapped - targetCol);
			if ((flee && dist > bestScore) || (!flee && dist < bestScore)) {
				bestScore = dist;
				best = { dir, row: nextRow, col: wrapped };
			}
		}
		if (!best && reverse !== 'none') {
			const [dr, dc] = this.deltas[reverse];
			const nextRow = row + dr;
			const nextCol = col + dc;
			if (!this.isWallCell(nextRow, nextCol)) {
				best = { dir: reverse, row: nextRow, col: this.wrapCol(nextCol) };
			}
		}
		if (best) {
			g.dir = best.dir;
			g.top = best.row * this.gridSize;
			g.left = best.col * this.gridSize;
		}
	}

	// Returns true if a life was lost (loop should bail for this tick).
	private checkGhostCollision(): boolean {
		for (const g of this.ghosts) {
			if (g.eaten) {
				continue;
			}
			if (g.top === this.pacman.top && g.left === this.pacman.left) {
				if (g.frightened) {
					this.eatGhost(g);
				} else {
					this.loseLife();
					return true;
				}
			}
		}
		return false;
	}

	private eatGhost(g: Ghost): void {
		this.ghostCombo = Math.min(this.ghostCombo + 1, 4);
		const points = 200 * Math.pow(2, this.ghostCombo - 1); // 200/400/800/1600
		this.score += points;
		this.commitHighScore();
		this.addPopup(g.top, g.left, `${points}`);
		g.eaten = true;
		g.frightened = false;
		this.beep(880, 0.15, 'triangle', 0.06);
	}

	private loseLife(): void {
		this.lives--;
		this.beep(140, 0.5, 'sawtooth', 0.07);
		if (this.lives <= 0) {
			this.lives = 0;
			this.gameState = 'gameover';
			this.commitHighScore();
			return;
		}
		this.gameState = 'dying';
		setTimeout(() => {
			this.resetActors();
			this.mode = 'scatter';
			this.modeTicks = 0;
			this.frightenedTicks = 0;
			this.gameState = 'ready';
		}, 900);
	}

	private checkWin(): void {
		if (this.pellets.length > 0) {
			return;
		}
		this.gameState = 'levelclear';
		this.beep(660, 0.3, 'triangle', 0.06);
		setTimeout(() => {
			this.level++;
			this.initLevel();
			this.gameState = 'ready';
		}, 1600);
	}

	// --- Modes & timers -----------------------------------------------------

	private updateModeTimer(): void {
		this.modeTicks++;
		const scatterLen = this.ticksFor(6000);
		const chaseLen = this.ticksFor(20000);
		if (this.mode === 'scatter' && this.modeTicks >= scatterLen) {
			this.mode = 'chase';
			this.modeTicks = 0;
			this.reverseGhosts();
		} else if (this.mode === 'chase' && this.modeTicks >= chaseLen) {
			this.mode = 'scatter';
			this.modeTicks = 0;
			this.reverseGhosts();
		}
	}

	private reverseGhosts(): void {
		for (const g of this.ghosts) {
			if (!g.eaten && !g.frightened) {
				g.dir = this.opposite(g.dir);
			}
		}
	}

	private ticksFor(ms: number): number {
		return Math.max(1, Math.round(ms / this.tickMs));
	}

	private opposite(dir: Dir): Dir {
		switch (dir) {
		case 'up':
			return 'down';
		case 'down':
			return 'up';
		case 'left':
			return 'right';
		case 'right':
			return 'left';
		default:
			return 'none';
		}
	}

	// --- Score popups -------------------------------------------------------

	private addPopup(top: number, left: number, text: string): void {
		const id = this.popupId++;
		this.popups.push({ id, top, left, text });
		setTimeout(() => {
			this.popups = this.popups.filter((p) => p.id !== id);
		}, 800);
	}

	// --- High score ---------------------------------------------------------

	private loadHighScore(): number {
		try {
			if (typeof localStorage === 'undefined') {
				return 0;
			}
			const raw = localStorage.getItem('pacman-highscore');
			return raw ? parseInt(raw, 10) || 0 : 0;
		} catch {
			return 0;
		}
	}

	private commitHighScore(): void {
		if (this.score <= this.highScore) {
			return;
		}
		this.highScore = this.score;
		try {
			if (typeof localStorage !== 'undefined') {
				localStorage.setItem('pacman-highscore', String(this.highScore));
			}
		} catch {
			// storage unavailable (private mode / SSR) — keep in-memory only
		}
	}

	// --- Controls -----------------------------------------------------------

	togglePause(): void {
		if (this.gameState === 'playing') {
			this.gameState = 'paused';
		} else if (this.gameState === 'paused') {
			this.gameState = 'playing';
		}
	}

	toggleSound(): void {
		this.soundOn = !this.soundOn;
	}

	restartGame(): void {
		this.lives = 3;
		this.level = 1;
		this.score = 0;
		this.timer = 0;
		this.initLevel();
		this.gameState = 'ready';
	}

	// --- View helpers -------------------------------------------------------

	get pacRotation(): string {
		const rot: Record<Dir, number> = { right: 0, down: 90, left: 180, up: -90, none: 0 };
		return `rotate(${rot[this.pacDir]}deg)`;
	}

	get formattedTime(): string {
		const mins = Math.floor(this.timer / 60);
		const secs = this.timer % 60;
		return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
	}

	get livesArray(): number[] {
		return Array.from({ length: this.lives });
	}

	// Frightened ghosts flash white in the last ~1.5s before the effect ends.
	ghostFlashing(g: Ghost): boolean {
		return g.frightened && !g.eaten && this.frightenedTicks > 0 && this.frightenedTicks < this.ticksFor(1500);
	}

	trackByIndex(index: number): number {
		return index;
	}

	trackByPopup(_index: number, popup: Popup): number {
		return popup.id;
	}

	// --- Audio (optional, lazily created on first gesture) ------------------

	private beep(freq: number, dur: number, type: OscillatorType, vol: number): void {
		if (!this.soundOn || typeof window === 'undefined') {
			return;
		}
		const Ctor = (window as any).AudioContext || (window as any).webkitAudioContext;
		if (!Ctor) {
			return;
		}
		if (!this.audioCtx) {
			this.audioCtx = new Ctor();
		}
		if (this.audioCtx.state === 'suspended') {
			this.audioCtx.resume();
		}
		const osc = this.audioCtx.createOscillator();
		const gain = this.audioCtx.createGain();
		osc.type = type;
		osc.frequency.value = freq;
		osc.connect(gain);
		gain.connect(this.audioCtx.destination);
		const now = this.audioCtx.currentTime;
		gain.gain.setValueAtTime(vol, now);
		gain.gain.exponentialRampToValueAtTime(0.0001, now + dur);
		osc.start(now);
		osc.stop(now + dur);
	}
}
